import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import { Panel, PANEL_SPACING_VARIANT_PRIMARY } from './Panel'
import { Collapsible } from '../Collapsible'

import tokens from '../../design/tokens'
import { TITLE_VARIANT_M, TITLE_VARIANT_S } from '../Title'
import { HelpIcon } from '../HelpIcon'

export const PANEL_ACCORDION_TITLE_VARIANT_M = TITLE_VARIANT_M
export const PANEL_ACCORDION_TITLE_VARIANT_S = TITLE_VARIANT_S
export const PANEL_ACCORDION_OPTION_VARIANT_SECONDARY = 'secondary'
export const PANEL_ACCORDION_OPTION_VARIANT_TERTIARY = 'tertiary'
export const PANEL_ACCORDION_OPTION_VARIANT_EXTRA_LIGHT = 'extraLight'

const IconArrowRight = () => (
  <svg
    width='6'
    height='10'
    viewBox='0 0 6 10'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M5.10714 4.79492C5.16667 4.85445 5.19643 4.9229 5.19643 5.00028C5.19643 5.07766 5.16667 5.14611 5.10714 5.20564L0.946429 9.36635C0.886905 9.42587 0.818452 9.45564 0.741071 9.45564C0.663691 9.45564 0.595238 9.42587 0.535714 9.36635L0.0892857 8.91992C0.0297619 8.8604 0 8.79195 0 8.71457C0 8.63718 0.0297619 8.56873 0.0892857 8.50921L3.59821 5.00028L0.0892857 1.49135C0.0297619 1.43183 0 1.36337 0 1.28599C0 1.20861 0.0297619 1.14016 0.0892857 1.08064L0.535714 0.634208C0.595238 0.574684 0.663691 0.544922 0.741071 0.544922C0.818452 0.544922 0.886905 0.574684 0.946429 0.634208L5.10714 4.79492Z'
      fill='currentColor'
    />
  </svg>
)

export const PanelAccordion = ({
  title,
  titleTooltip,
  panelVariant,
  children,
  className,
  onClick,
  disabled,
  collapsed,
  onChange,
  'data-mu-test': dataAttributeTest,
  margin
}) => {
  return (
    <Panel
      className={className}
      data-mu-test={dataAttributeTest}
      onClick={onClick}
      spacingVariant={PANEL_SPACING_VARIANT_PRIMARY}
      disabled={disabled}
      margin={margin}
    >
      <StyledTitleContainer>
        <StyledTrigger
          onClick={onChange}
          $disabled={disabled}
          $variant={panelVariant}
          $collapsed={collapsed}
        >
          <StyledIconContainer $collapsed={collapsed}>
            <IconArrowRight />
          </StyledIconContainer>
          <span>{title}</span>
        </StyledTrigger>
        {titleTooltip && <HelpIcon text={titleTooltip} inline={false} />}
      </StyledTitleContainer>
      {children && (
        <StyledCollapsible
          data-mu-test={dataAttributeTest && `${dataAttributeTest}-children`}
          collapsed={collapsed}
        >
          {children}
        </StyledCollapsible>
      )}
    </Panel>
  )
}

// PROPS

PanelAccordion.defaultProps = {
  titleVariant: PANEL_ACCORDION_TITLE_VARIANT_M,
  accordionVariant: PANEL_ACCORDION_OPTION_VARIANT_SECONDARY,
  collapsed: false,
  margin: `0 0 ${tokens.spacing.large} 0`
}

PanelAccordion.propTypes = {
  title: PropTypes.string,
  titleTooltip: PropTypes.string,
  panelVariant: PropTypes.oneOf([
    PANEL_ACCORDION_OPTION_VARIANT_SECONDARY,
    PANEL_ACCORDION_OPTION_VARIANT_TERTIARY,
    PANEL_ACCORDION_OPTION_VARIANT_EXTRA_LIGHT
  ]),
  children: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  collapsed: PropTypes.bool,
  'data-mu-test': PropTypes.string,
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

// STYLE
const StyledTitleContainer = styled.div`
  display: flex;
  align-items: center;
`

const styleVariant = ({ $variant, $collapsed }) => {
  switch ($variant) {
    case PANEL_ACCORDION_OPTION_VARIANT_SECONDARY:
      return `
      font-weight: ${tokens.fontWeight.bold};
        text-decoration:none;
        color: ${
          $collapsed
            ? tokens.color.link.highlight.link
            : tokens.color.link.highlight.hover
        };
        :hover {
          text-decoration:none;
          color: ${tokens.color.link.highlight.hover};
        }
      `
    case PANEL_ACCORDION_OPTION_VARIANT_TERTIARY:
      return `
      font-weight: ${tokens.fontWeight.bold};
        text-decoration:none;
        color: ${
          $collapsed
            ? tokens.color.link.default.link
            : tokens.color.link.default.hover
        };
        :hover {
          text-decoration:none;
          color: ${tokens.color.link.default.hover};
        }
      `
    case PANEL_ACCORDION_OPTION_VARIANT_EXTRA_LIGHT:
      return `
      font-weight: ${tokens.fontWeight.normal};
        text-decoration:underline;
        color: ${
          $collapsed
            ? tokens.color.link.extralight.link
            : tokens.color.link.extralight.hover
        };
        :hover {
          text-decoration:underline;
          color: ${tokens.color.link.extralight.hover};
        }
      
      `
  }
}

const StyledTrigger = styled.div`
  display: flex;
  align-items: center;

  font-size: ${tokens.fontSize.standard};

  transition: ${tokens.transition.color};

  ${({ $disabled }) => ($disabled ? 'pointer-events:none;' : '')}
  ${styleVariant}


  cursor: pointer;
`
const StyledIconContainer = styled.i`
  display: inline-flex;
  align-items: center;

  width: 16px;
  height: 16px;

  position: relative;

  > svg {
    transition: transform 0.2s ease;
    transform-origin: 3px 5px;
    ${({ $collapsed }) => (!$collapsed ? 'transform: rotate(90deg);' : '')}
  }
`

const StyledCollapsible = styled(Collapsible)`
  .e-collapsible__inner {
    margin-top: ${tokens.spacing.small};
    margin-bottom: ${tokens.spacing.small};
    margin-left: ${tokens.spacing.medium};
  }
`
